import { productNormalizer } from './ProductNormalizer'
import type { ParsedProductHints, ProductModel } from './ProductTypes'

/**
 * ProductAlias — many textual forms map to one product (exclusive ownership).
 */
export class ProductAlias {
  private aliasToFingerprint = new Map<string, string>()

  clear(): void {
    this.aliasToFingerprint.clear()
  }

  normalizeAlias(alias: string): string {
    return productNormalizer.normalize(alias)
  }

  resolve(alias: string): string | null {
    return this.aliasToFingerprint.get(this.normalizeAlias(alias)) ?? null
  }

  /**
   * Registers alias for fingerprint. Returns false if alias already owned by another product.
   */
  register(alias: string, fingerprint: string): boolean {
    const key = this.normalizeAlias(alias)
    if (!key) return false
    const existing = this.aliasToFingerprint.get(key)
    if (existing && existing !== fingerprint) return false
    this.aliasToFingerprint.set(key, fingerprint)
    return true
  }

  releaseFingerprint(fingerprint: string): void {
    for (const [alias, fp] of this.aliasToFingerprint) {
      if (fp === fingerprint) this.aliasToFingerprint.delete(alias)
    }
  }

  buildAliases(hints: ParsedProductHints, fingerprint: string): string[] {
    const aliases = new Set<string>()
    const push = (value: string) => {
      const n = productNormalizer.collapse(value)
      if (n) aliases.add(n)
    }

    push(hints.raw)
    push(`${hints.brand} ${hints.model}`.trim())
    push(`${hints.category} ${hints.brand} ${hints.model}`.trim())
    push(`${hints.brand} ${hints.category} ${hints.model}`.trim())
    push(`${hints.model}`)
    if (hints.line) {
      push(`${hints.brand} ${hints.line} ${hints.model}`.trim())
      push(`${hints.line} ${hints.model}`.trim())
    }
    if (hints.partNumber) {
      push(hints.partNumber)
      push(`${hints.brand} ${hints.partNumber}`)
    }
    if (hints.capacity) {
      push(`${hints.brand} ${hints.model} ${hints.capacity}`)
      push(`${hints.category} ${hints.brand} ${hints.model} ${hints.capacity}`)
    }
    for (const tech of hints.technology) {
      push(`${hints.brand} ${hints.model} ${tech}`)
    }

    // Ensure exclusivity registration attempt list
    const list = [...aliases].filter(Boolean)
    for (const alias of list) {
      this.register(alias, fingerprint)
    }
    return list
  }

  mergeProductAliases(product: ProductModel, extra: string[]): string[] {
    const set = new Set(product.aliases.map((a) => productNormalizer.collapse(a)))
    for (const alias of extra) {
      const ok = this.register(alias, product.fingerprint)
      if (ok) set.add(productNormalizer.collapse(alias))
    }
    return [...set]
  }

  size(): number {
    return this.aliasToFingerprint.size
  }
}

export const productAlias = new ProductAlias()
